import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import type {
  LookerDataset,
  LookerReportGranularity,
  PeriodicReport,
  PeriodicReportErrorCode,
} from '@brand24-original/llm-types';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import { PeriodicReportService } from '../../../../shared/services/integrations/PeriodicReportService';
import { LookerStudioService } from '../../../../shared/services/integrations/LookerStudioService';
import ColorTag from '../../../../shared/components/color-tag/ColorTag';
import {
  availableLookerDatasets,
  currentLookerParameters,
  formatLookerDay,
  getBuildStateColor,
  getBuildStateName,
  getDatasetName,
  getTypeName,
  isLegacyLookerReport,
  isSavedLookerStudioReportUrl,
  LOOKER_DATASETS,
  LOOKER_GRANULARITIES,
  lookerDataStart,
  lookerInitialStart,
} from '../../../../shared/helpers/periodic-reports/PeriodicReportsHelper';
// Globalne `.tabs` — importowane wprost, bo ten ekran nie musi ładować komponentu `Tabs`.
import '../../../../shared/components/tabs/Tabs.scss';
import './ReportSetupDialog.scss';

interface ReportSetupDialogProps {
  visible: boolean;
  /** Raport, który czeka na link — okno otwiera się od razu na kroku 2. `null` = nowy raport (krok 1). */
  report: PeriodicReport | null;
  onHide: () => void;
  onReportCreated: (report: PeriodicReport) => void;
  onLinkSaved: () => void;
}

const STEPS = ['lookerSetupStepConfigure', 'lookerSetupStepLooker'];

/** Step 2 re-reads a report whose sheet is still filling this often — the list's own poll pauses while the dialog is open. */
const BUILD_POLL_MS = 10_000;

/**
 * Jedno okno zakładania raportu Looker Studio, w dwóch krokach: wybór projektu (tworzy arkusz Google
 * z danymi), a potem instrukcja zapisania raportu w Looker Studio i wklejenia jego adresu. Wygląd jak
 * okno marki na stronie Brands (`BrandDialog`): nagłówek, zakładki-kroki, ustawienia jako karty.
 */
export const ReportSetupDialog: React.FC<ReportSetupDialogProps> = ({
  visible,
  report,
  onHide,
  onReportCreated,
  onLinkSaved,
}) => {
  const { t } = useTranslation();
  const activeStep = report ? 1 : 0;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      className="dialog-box dialog-box--no-footer report-setup-dialog"
      draggable={false}
      header={t('lookerSetupTitle')}
    >
      <div className="tabs report-setup-dialog__steps">
        {STEPS.map((labelKey, index) => (
          <span
            key={labelKey}
            className={`tabs__item${index === activeStep ? ' tabs__item--active' : ''}${
              index < activeStep ? ' report-setup-dialog__step--done' : ''
            }`}
          >
            <span className="report-setup-dialog__step-number">{index < activeStep ? '✓' : index + 1}</span>
            {t(labelKey)}
          </span>
        ))}
      </div>

      <div className="report-setup-dialog__body">
        {report ? (
          // `key` = raport: szkic adresu nie może przejść z jednego raportu na drugi.
          <LookerStep key={report.id} report={report} onLinkSaved={onLinkSaved} onSkip={onHide} />
        ) : (
          <ProjectStep onReportCreated={onReportCreated} />
        )}
      </div>
    </Dialog>
  );
};

const ProjectStep: React.FC<Pick<ReportSetupDialogProps, 'onReportCreated'>> = ({ onReportCreated }) => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [granularity, setGranularity] = useState<LookerReportGranularity>('DAILY');
  const [datasets, setDatasets] = useState<LookerDataset[]>([]);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [existingReports, setExistingReports] = useState<PeriodicReport[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tylko projekty WŁASNEGO konta: superadmin ma w kontekście projekty wszystkich klientów, a raport
  // (arkusz i codzienne odświeżanie) liczy się na koncie zalogowanego — cudzy projekt dałby pusty arkusz.
  // Demo też należy do innego konta.
  const projects = useMemo(
    () =>
      (projectResponse || [])
        .filter((project) => project.accountId === accountResponse?.accountId && !project.demoVersion)
        .sort((a, b) => a.projectLabel.localeCompare(b.projectLabel)),
    [accountResponse, projectResponse]
  );

  // Own-account projects only, so the owner's addons ARE the account's. A dataset the addons do not
  // cover is not offered at all — the backend refuses it with the same rule.
  const addons = accountResponse?.addons;
  const visibleDatasets = useMemo(() => availableLookerDatasets(addons), [addons]);
  useEffect(() => setDatasets(visibleDatasets), [visibleDatasets, selectedProject?.id]);

  useEffect(() => {
    LookerStudioService.getConnectionStatus()
      .then((status) => setIsConnected(status.isConnected))
      .catch(() => setIsConnected(false));
    PeriodicReportService.getPeriodicReports(undefined, 'LOOKER_STUDIO')
      .then(setExistingReports)
      .catch(() => setExistingReports([]));
  }, []);

  // One report per (project, granularity) in the current format; a legacy daily report does not block
  // a new daily one — the backend applies the same rule (409 otherwise).
  const projectHasReport =
    !!selectedProject &&
    existingReports.some(
      (item) =>
        item.projectId === selectedProject.id &&
        item.provider === 'LOOKER_STUDIO' &&
        item.type === granularity &&
        !isLegacyLookerReport(item)
    );

  const toggleDataset = (dataset: LookerDataset, checked: boolean) =>
    setDatasets((current) =>
      checked ? visibleDatasets.filter((d) => d === dataset || current.includes(d)) : current.filter((d) => d !== dataset)
    );

  const handleCreate = async () => {
    if (!selectedProject) {
      setError(t('projectRequired'));
      return;
    }
    setIsCreating(true);
    setError(null);
    try {
      const created = await LookerStudioService.createReport({
        projectId: selectedProject.id,
        granularity,
        datasets,
        reportName: `${selectedProject.projectLabel} - ${getTypeName(granularity)}`,
      });
      onReportCreated(created);
    } catch (createError: any) {
      const data = createError?.response?.data;
      const code: PeriodicReportErrorCode | undefined = data?.code;
      if (code === 'INSUFFICIENT_SCOPES') {
        setError(t('googlePermissionsChanged'));
        setIsConnected(false);
      } else if (code === 'REPORT_ALREADY_EXISTS') {
        setError(t('lookerReportExistsForGranularity'));
      } else if (code === 'DATASET_NOT_AVAILABLE') {
        setError(t('lookerDatasetNotAvailable'));
      } else {
        const detail = data?.message || data?.error;
        setError(detail ? `${t('failedToCreateReport')}: ${detail}` : t('failedToCreateReport'));
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="report-setup-dialog__section">
      <DataTimeline granularity={granularity} />

      <div className="report-setup-dialog__setting">
        <div className="report-setup-dialog__setting-head">
          <span className="report-setup-dialog__setting-title">{t('project')}</span>
          <span className="report-setup-dialog__setting-desc">{t('lookerSetupProjectDesc')}</span>
        </div>
        <Dropdown
          value={selectedProject}
          options={projects}
          onChange={(event) => {
            setSelectedProject(event.value);
            // The granularity the project opens with on the panel (weekly once the data is older than a month).
            setGranularity(event.value?.defaultPeriod ?? 'DAILY');
            setError(null);
          }}
          optionLabel="projectLabel"
          placeholder={t('selectProject')}
          className="dropdown dropdown--with-clear"
          disabled={projects.length === 0}
          filter
          filterPlaceholder={t('searchProjects')}
          filterBy="projectLabel"
          showClear
        />
      </div>

      <div className="report-setup-dialog__setting">
        <div className="report-setup-dialog__setting-head">
          <span className="report-setup-dialog__setting-title">{t('lookerGranularityTitle')}</span>
          <span className="report-setup-dialog__setting-desc">{t('lookerGranularityDesc')}</span>
        </div>
        <div className="report-setup-dialog__options">
          {LOOKER_GRANULARITIES.map((option) => (
            <label key={option} className="report-setup-dialog__option" htmlFor={`looker-granularity-${option}`}>
              <RadioButton
                inputId={`looker-granularity-${option}`}
                name="looker-granularity"
                value={option}
                checked={granularity === option}
                onChange={() => {
                  setGranularity(option);
                  setError(null);
                }}
              />
              {getTypeName(option)}
            </label>
          ))}
        </div>
      </div>

      <div className="report-setup-dialog__setting">
        <div className="report-setup-dialog__setting-head">
          <span className="report-setup-dialog__setting-title">{t('lookerDatasetsTitle')}</span>
          <span className="report-setup-dialog__setting-desc">{t('lookerDatasetsDesc')}</span>
        </div>
        <div className="report-setup-dialog__options">
          {visibleDatasets.map((dataset) => (
            <label key={dataset} className="report-setup-dialog__option" htmlFor={`looker-dataset-${dataset}`}>
              <Checkbox
                inputId={`looker-dataset-${dataset}`}
                checked={datasets.includes(dataset)}
                onChange={(event) => toggleDataset(dataset, !!event.checked)}
              />
              {getDatasetName(dataset)}
            </label>
          ))}
        </div>
        {datasets.length === 0 && <p className="report-setup-dialog__error">{t('lookerDatasetRequired')}</p>}
      </div>

      {isConnected === false && <p className="report-setup-dialog__error">{t('notConnectedToLookerStudio')}</p>}
      {projectHasReport && <p className="report-setup-dialog__error">{t('lookerReportExistsForGranularity')}</p>}
      {error && <p className="report-setup-dialog__error">{error}</p>}

      <div className="report-setup-dialog__footer">
        <button
          type="button"
          className="btn"
          onClick={handleCreate}
          disabled={isCreating || !selectedProject || projectHasReport || isConnected !== true || datasets.length === 0}
        >
          {isCreating ? t('creating') : t('next')}
          <span className="ico--right ico--xs" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const GRANULARITY_KEY_SUFFIX: Readonly<Record<LookerReportGranularity, string>> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

/**
 * How the sheet fills up for the granularity — the start window, the nightly step, the history cap: a
 * one-line teaser in the dialog, the details in a tooltip on it. `dataStart` = an existing report's
 * first day (step 2); without it, the start a new report would get (step 1).
 */
const DataTimeline: React.FC<{ granularity: LookerReportGranularity; dataStart?: Date | null }> = ({
  granularity,
  dataStart = null,
}) => {
  const { t, i18n } = useTranslation();
  const suffix = GRANULARITY_KEY_SUFFIX[granularity];
  const startDate = formatLookerDay(dataStart ?? lookerInitialStart(granularity), i18n.language);
  const rows = [
    dataStart
      ? { label: t('lookerTimelineNowLabel'), text: t('lookerTimelineNow', { date: startDate }) }
      : {
          label: t('lookerTimelineStartLabel'),
          text: `${t(`lookerTimelineStart${suffix}`, { date: startDate })} ${t('lookerTimelineStartShortHistory')}`,
        },
    { label: t('lookerTimelineNightlyLabel'), text: t(`lookerTimelineNightly${suffix}`) },
    { label: t('lookerTimelineHistoryLabel'), text: t('lookerTimelineHistory', { max: t(`lookerRetention${suffix}`) }) },
  ];

  return (
    <>
      <div id="looker-data-timeline" className="report-setup-dialog__timeline" role="note" tabIndex={0}>
        <span className="ico--info ico--xs" aria-hidden="true" />
        {/* An existing report names its own first day; a new one says what it will start with. */}
        <span>{dataStart ? t('lookerTimelineTeaserReport', { date: startDate }) : t('lookerTimelineTeaser')}</span>
      </div>
      {/* appendTo body: inside the dialog the tooltip would be clipped by its scroll container. */}
      <Tooltip
        target="#looker-data-timeline"
        position="top"
        className="tooltip--top report-setup-dialog__timeline-tooltip"
        appendTo={document.body}
      >
        <span className="report-setup-dialog__timeline-title">{t('lookerTimelineTitle')}</span>
        <dl className="report-setup-dialog__timeline-list">
          {rows.map((row) => (
            <div key={row.label} className="report-setup-dialog__timeline-row">
              <dt className="report-setup-dialog__timeline-label">{row.label}</dt>
              <dd className="report-setup-dialog__timeline-text">{row.text}</dd>
            </div>
          ))}
        </dl>
      </Tooltip>
    </>
  );
};

interface LookerStepProps {
  report: PeriodicReport;
  onLinkSaved: () => void;
  onSkip: () => void;
}

const LookerStep: React.FC<LookerStepProps> = ({ report: initialReport, onLinkSaved, onSkip }) => {
  const { t } = useTranslation();
  const [report, setReport] = useState<PeriodicReport>(initialReport);
  const [url, setUrl] = useState<string>(report.config?.parameters?.finalReportUrl || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Legacy reports (one flat tab) keep their original copy; the current format has a tab per dataset.
  const current = currentLookerParameters(report);
  // Looker Studio reads a tab's columns and their types once, when the report is first opened: opened on
  // a sheet that is still filling, it gets no fields and its charts stay broken. Until the first build
  // has filled the sheet, opening is locked. Legacy reports are filled at creation.
  const awaitsFirstFill = !!current && !current.lastRefreshAt;
  const isBuilding = awaitsFirstFill && current.build.state === 'BUILDING';
  const isStalled = awaitsFirstFill && (current.build.state === 'FAILED' || current.health.state !== 'OK');

  useEffect(() => {
    if (!isBuilding) return undefined;
    const timer = setInterval(() => {
      PeriodicReportService.getPeriodicReport(report.id)
        .then(setReport)
        .catch(() => undefined);
    }, BUILD_POLL_MS);
    return () => clearInterval(timer);
  }, [isBuilding, report.id]);

  const openLookerStudio = async () => {
    setError(null);
    try {
      const result = await LookerStudioService.getReportUrl(report.id);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch {
      setError(t('failedToOpenReport'));
    }
  };

  const save = async () => {
    const value = url.trim();
    if (!isSavedLookerStudioReportUrl(value)) {
      setError(t('invalidLookerStudioUrl'));
      return;
    }
    setIsSaving(true);
    setError(null);
    const saved = await LookerStudioService.saveFinalReportUrl(report.id, value);
    setIsSaving(false);
    if (saved) {
      onLinkSaved();
    } else {
      setError(t('failedToSaveReportUrl'));
    }
  };

  const reportName = report.config?.parameters?.reportName ?? '';
  const granularity = LOOKER_GRANULARITIES.find((option) => option === report.type);
  const dataStart = current && granularity ? lookerDataStart(granularity, current) : null;
  const otherTabs = current?.datasets.slice(1).map(getDatasetName) ?? [];

  return (
    <div className="report-setup-dialog__section">
      {/* Same spot as in step 1: the top of the step. */}
      {granularity && current && <DataTimeline granularity={granularity} dataStart={dataStart} />}

      <p className="report-setup-dialog__intro">
        {t(awaitsFirstFill ? 'lookerSetupIntroBuilding' : 'lookerSetupIntro', { name: reportName })}
      </p>

      <div className="report-setup-dialog__setting report-setup-dialog__setting--inline">
        <div className="report-setup-dialog__setting-head">
          <span className="report-setup-dialog__setting-title">1. {t('lookerSetupOpenTitle')}</span>
          <span className="report-setup-dialog__setting-desc">
            {!current
              ? t('lookerSetupOpenDescLegacy')
              : current.templateReportId
                ? t('lookerSetupOpenDescTemplate')
                : t('lookerSetupOpenDesc', { dataset: getDatasetName(current.datasets[0]) })}
          </span>
          {awaitsFirstFill &&
            (isStalled ? (
              <span className="report-setup-dialog__error">{t('lookerSetupBuildStalled')}</span>
            ) : (
              <span className="report-setup-dialog__building">
                {/* The same chip as the list's build column. */}
                <ColorTag color={getBuildStateColor('BUILDING')}>
                  {getBuildStateName('BUILDING')}
                  {current.build.totalPeriods > 0 ? ` ${current.build.completedPeriods}/${current.build.totalPeriods}` : ''}
                </ColorTag>
                {t('lookerSetupBuildingWait')}
              </span>
            ))}
        </div>
        <button type="button" className="btn btn--sm" onClick={openLookerStudio} disabled={awaitsFirstFill}>
          {t('lookerSetupOpenBtn')}
        </button>
      </div>

      <div className="report-setup-dialog__setting">
        <div className="report-setup-dialog__setting-head">
          <span className="report-setup-dialog__setting-title">2. {t('lookerSetupSaveTitle')}</span>
          <span className="report-setup-dialog__setting-desc">{t('lookerSetupSaveDesc')}</span>
          {current?.templateReportId && current.datasets.length < LOOKER_DATASETS.length && (
            <span className="report-setup-dialog__setting-desc">{t('lookerSetupTemplateEmptyPages')}</span>
          )}
          {/* The default Looker report holds one data source: the other tabs are added from the same sheet. */}
          {!current?.templateReportId && otherTabs.length > 0 && (
            <span className="report-setup-dialog__setting-desc">
              {t('lookerSetupAddTabsDesc', { tabs: otherTabs.join(', ') })}
            </span>
          )}
        </div>
      </div>

      <div className="report-setup-dialog__setting">
        <div className="report-setup-dialog__setting-head">
          <span className="report-setup-dialog__setting-title">3. {t('lookerSetupPasteTitle')}</span>
          <span className="report-setup-dialog__setting-desc">{t('lookerSetupPasteDesc')}</span>
        </div>
        <div className="report-setup-dialog__url">
          <InputText
            className="report-setup-dialog__input"
            value={url}
            placeholder="https://lookerstudio.google.com/reporting/…"
            disabled={isSaving}
            onChange={(event) => {
              setUrl(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void save();
              }
            }}
          />
          <button type="button" className="btn btn--sm" onClick={save} disabled={!url.trim() || isSaving}>
            {t('saveUrl')}
          </button>
        </div>
        {error && <p className="report-setup-dialog__error">{error}</p>}
      </div>

      {!current && <p className="report-setup-dialog__intro">{t('lookerSetupRefreshNoteLegacy')}</p>}

      {/* Link służy tylko otwieraniu raportu z Chatbeat — można go wkleić później z menu wiersza. */}
      {!report.config?.parameters?.finalReportUrl && (
        <div className="report-setup-dialog__footer">
          <button type="button" className="btn btn--secondary" onClick={onSkip}>
            {t('lookerSetupSkip')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportSetupDialog;
